import type { ReactiveController, ReactiveControllerHost } from 'lit'

interface Options<T> {
  channelName: string
  onMessage: (message: T) => void
  enabled?: () => boolean
  /**
   * When the document is hidden, incoming messages are buffered instead of
   * delivered, and successive messages are coalesced with this reducer
   * (default: keep the latest). The buffered value is delivered once, when the
   * document becomes visible again.
   *
   * This stops background tabs from doing work (e.g. layout reflows) they
   * cannot show. On machines where same-origin tabs share a single renderer
   * process/main thread — common on low-memory devices — that background work
   * would otherwise steal main-thread time from the foreground tab and degrade
   * its interaction latency (INP).
   */
  coalesce?: (pending: T | undefined, incoming: T) => T
}

const defaultEnabled = () => true
const defaultCoalesce = <T>(_pending: T | undefined, incoming: T): T => incoming

export class BroadcastChannelController<T> implements ReactiveController {
  private channel?: BroadcastChannel
  private options: Required<Options<T>>
  // Wrapped so `undefined` (no buffered message) is distinguishable from a
  // buffered value that happens to be `undefined`.
  private pending?: { value: T }

  constructor(host: ReactiveControllerHost, options: Options<T>) {
    host.addController(this)
    this.options = { enabled: defaultEnabled, coalesce: defaultCoalesce, ...options }
  }

  hostConnected(): void {
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(this.options.channelName)
      this.channel.addEventListener('message', this)
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange)
    }
  }

  hostDisconnected(): void {
    this.channel?.removeEventListener('message', this)
    this.channel?.close()
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    }
    this.pending = undefined
  }

  handleEvent(event: MessageEvent<T>) {
    if (!this.options.enabled()) {
      return
    }
    // Defer work in hidden tabs: buffer the coalesced latest value and apply it
    // once the tab is visible again. A burst of messages received while hidden
    // collapses into a single delivery.
    if (typeof document !== 'undefined' && document.hidden) {
      this.pending = { value: this.options.coalesce(this.pending?.value, event.data) }
      return
    }
    this.options.onMessage(event.data)
  }

  private handleVisibilityChange = () => {
    if (typeof document !== 'undefined' && document.hidden) {
      return
    }
    const pending = this.pending
    this.pending = undefined
    if (pending && this.options.enabled()) {
      this.options.onMessage(pending.value)
    }
  }

  post(message: T) {
    if (this.options.enabled()) {
      this.channel?.postMessage(message)
    }
  }
}
